/**
 * canonical-presets.ts
 *
 * Canonical W3C DTCG Theme Presets (Sprint THEME-02):
 * - Sovereign Dark, Sovereign Light (Daylight), Midnight Nordic, Industrial Amber, and High Contrast.
 */

import type { ThemeDefinition } from '../theme-contracts.js';

export const SOVEREIGN_DARK: ThemeDefinition = {
  themeId: 'sovereign-dark',
  displayName: 'Sovereign Dark',
  isDark: true,
  colors: {
    surfaceCanvas: { $value: '#090d16', $type: 'color', $description: 'Root application background' },
    surfacePanel: { $value: '#0f172a', $type: 'color', $description: 'Card, modal, and drawer panels' },
    surfacePanelSubtle: { $value: '#1e293b', $type: 'color', $description: 'Subtle secondary panel containers' },
    borderSubtle: { $value: '#1e293b', $type: 'color', $description: 'Subtle divider borders' },
    borderStrong: { $value: '#334155', $type: 'color', $description: 'Active card and input borders' },
    textPrimary: { $value: '#f8fafc', $type: 'color', $description: 'High-contrast primary headings and text' },
    textSecondary: { $value: '#94a3b8', $type: 'color', $description: 'Secondary labels and descriptive text' },
    textMuted: { $value: '#64748b', $type: 'color', $description: 'Disabled text and placeholders' },
    brandPrimary: { $value: '#38bdf8', $type: 'color', $description: 'Primary action and brand accent' },
    brandPrimaryHover: { $value: '#0284c7', $type: 'color', $description: 'Brand button hover state' },
    brandOnPrimary: { $value: '#090d16', $type: 'color', $description: 'Text rendered on primary brand fill' },
    accentSuccess: { $value: '#10b981', $type: 'color', $description: 'Positive state / Stock settled' },
    accentWarning: { $value: '#f59e0b', $type: 'color', $description: 'Warning state / Approaching SLA' },
    accentDanger: { $value: '#ef4444', $type: 'color', $description: 'Error state / Credit blocked' },
    focusRing: { $value: '#0284c7', $type: 'color', $description: 'Keyboard accessibility focus outline' },
  },
  geometry: {
    radiusCard: { $value: '12px', $type: 'dimension', $description: 'Corner radius for cards and modals' },
    radiusControl: { $value: '6px', $type: 'dimension', $description: 'Corner radius for buttons and inputs' },
    fontSans: { $value: 'Inter, system-ui, -apple-system, sans-serif', $type: 'fontFamily' },
    fontMono: { $value: '"JetBrains Mono", ui-monospace, monospace', $type: 'fontFamily' },
  },
};

export const SOVEREIGN_LIGHT: ThemeDefinition = {
  themeId: 'sovereign-light',
  displayName: 'Sovereign Daylight (Light)',
  isDark: false,
  colors: {
    surfaceCanvas: { $value: '#f8fafc', $type: 'color', $description: 'Root daylight background' },
    surfacePanel: { $value: '#ffffff', $type: 'color', $description: 'Card and drawer surface' },
    surfacePanelSubtle: { $value: '#f1f5f9', $type: 'color', $description: 'Subtle gray containers' },
    borderSubtle: { $value: '#e2e8f0', $type: 'color', $description: 'Subtle borders' },
    borderStrong: { $value: '#cbd5e1', $type: 'color', $description: 'Strong input borders' },
    textPrimary: { $value: '#0f172a', $type: 'color', $description: 'Primary readable dark text' },
    textSecondary: { $value: '#334155', $type: 'color', $description: 'Muted slate descriptions' },
    textMuted: { $value: '#64748b', $type: 'color', $description: 'Placeholder text' },
    brandPrimary: { $value: '#0369a1', $type: 'color', $description: 'Deep sky blue brand fill' },
    brandPrimaryHover: { $value: '#075985', $type: 'color', $description: 'Brand button hover state' },
    brandOnPrimary: { $value: '#ffffff', $type: 'color', $description: 'White text on brand button' },
    accentSuccess: { $value: '#059669', $type: 'color', $description: 'Positive state' },
    accentWarning: { $value: '#d97706', $type: 'color', $description: 'Warning state' },
    accentDanger: { $value: '#dc2626', $type: 'color', $description: 'Error state' },
    focusRing: { $value: '#0284c7', $type: 'color', $description: 'Focus outline' },
  },
  geometry: {
    radiusCard: { $value: '12px', $type: 'dimension' },
    radiusControl: { $value: '6px', $type: 'dimension' },
    fontSans: { $value: 'Inter, system-ui, sans-serif', $type: 'fontFamily' },
    fontMono: { $value: '"JetBrains Mono", monospace', $type: 'fontFamily' },
  },
};

export const MIDNIGHT_NORDIC: ThemeDefinition = {
  themeId: 'midnight-nordic',
  displayName: 'Midnight Nordic',
  isDark: true,
  colors: {
    surfaceCanvas: { $value: '#0b1120', $type: 'color' },
    surfacePanel: { $value: '#131d32', $type: 'color' },
    surfacePanelSubtle: { $value: '#1c2b48', $type: 'color' },
    borderSubtle: { $value: '#1e293b', $type: 'color' },
    borderStrong: { $value: '#2d3f63', $type: 'color' },
    textPrimary: { $value: '#f0f6fc', $type: 'color' },
    textSecondary: { $value: '#8b9bb4', $type: 'color' },
    textMuted: { $value: '#546682', $type: 'color' },
    brandPrimary: { $value: '#60a5fa', $type: 'color' },
    brandPrimaryHover: { $value: '#3b82f6', $type: 'color' },
    brandOnPrimary: { $value: '#0b1120', $type: 'color' },
    accentSuccess: { $value: '#34d399', $type: 'color' },
    accentWarning: { $value: '#fbbf24', $type: 'color' },
    accentDanger: { $value: '#f87171', $type: 'color' },
    focusRing: { $value: '#60a5fa', $type: 'color' },
  },
  geometry: {
    radiusCard: { $value: '10px', $type: 'dimension' },
    radiusControl: { $value: '5px', $type: 'dimension' },
    fontSans: { $value: 'system-ui, sans-serif', $type: 'fontFamily' },
    fontMono: { $value: 'monospace', $type: 'fontFamily' },
  },
};

export const INDUSTRIAL_AMBER: ThemeDefinition = {
  themeId: 'industrial-amber',
  displayName: 'Industrial Amber (Warehouse/Kiosk)',
  isDark: true,
  colors: {
    surfaceCanvas: { $value: '#121212', $type: 'color' },
    surfacePanel: { $value: '#1e1e1e', $type: 'color' },
    surfacePanelSubtle: { $value: '#2a2a2a', $type: 'color' },
    borderSubtle: { $value: '#333333', $type: 'color' },
    borderStrong: { $value: '#4f4f4f', $type: 'color' },
    textPrimary: { $value: '#ffffff', $type: 'color' },
    textSecondary: { $value: '#d1d1d1', $type: 'color' },
    textMuted: { $value: '#888888', $type: 'color' },
    brandPrimary: { $value: '#f59e0b', $type: 'color' },
    brandPrimaryHover: { $value: '#d97706', $type: 'color' },
    brandOnPrimary: { $value: '#121212', $type: 'color' },
    accentSuccess: { $value: '#10b981', $type: 'color' },
    accentWarning: { $value: '#fbbf24', $type: 'color' },
    accentDanger: { $value: '#ef4444', $type: 'color' },
    focusRing: { $value: '#f59e0b', $type: 'color' },
  },
  geometry: {
    radiusCard: { $value: '4px', $type: 'dimension' },
    radiusControl: { $value: '2px', $type: 'dimension' },
    fontSans: { $value: 'system-ui, sans-serif', $type: 'fontFamily' },
    fontMono: { $value: 'monospace', $type: 'fontFamily' },
  },
};

export const HIGH_CONTRAST: ThemeDefinition = {
  themeId: 'high-contrast',
  displayName: 'High Contrast (Accessibility)',
  isDark: true,
  colors: {
    surfaceCanvas: { $value: '#000000', $type: 'color' },
    surfacePanel: { $value: '#000000', $type: 'color' },
    surfacePanelSubtle: { $value: '#1a1a1a', $type: 'color' },
    borderSubtle: { $value: '#ffffff', $type: 'color' },
    borderStrong: { $value: '#ffffff', $type: 'color' },
    textPrimary: { $value: '#ffffff', $type: 'color' },
    textSecondary: { $value: '#ffffff', $type: 'color' },
    textMuted: { $value: '#cccccc', $type: 'color' },
    brandPrimary: { $value: '#ffff00', $type: 'color' },
    brandPrimaryHover: { $value: '#e6e600', $type: 'color' },
    brandOnPrimary: { $value: '#000000', $type: 'color' },
    accentSuccess: { $value: '#00ff00', $type: 'color' },
    accentWarning: { $value: '#ffaa00', $type: 'color' },
    accentDanger: { $value: '#ff0000', $type: 'color' },
    focusRing: { $value: '#ffff00', $type: 'color' },
  },
  geometry: {
    radiusCard: { $value: '0px', $type: 'dimension' },
    radiusControl: { $value: '0px', $type: 'dimension' },
    fontSans: { $value: 'system-ui, sans-serif', $type: 'fontFamily' },
    fontMono: { $value: 'monospace', $type: 'fontFamily' },
  },
};

export const EMERALD_MATRIX: ThemeDefinition = {
  themeId: 'emerald-matrix',
  displayName: 'Emerald Matrix (Tactical Defense)',
  isDark: true,
  colors: {
    surfaceCanvas: { $value: '#05130b', $type: 'color', $description: 'Deep obsidian forest base' },
    surfacePanel: { $value: '#0a2315', $type: 'color', $description: 'Tactical command panel' },
    surfacePanelSubtle: { $value: '#0f331f', $type: 'color', $description: 'Secondary matrix panel' },
    borderSubtle: { $value: '#14432a', $type: 'color', $description: 'Muted grid borders' },
    borderStrong: { $value: '#10b981', $type: 'color', $description: 'Active scanning border' },
    textPrimary: { $value: '#ecfdf5', $type: 'color', $description: 'High-contrast luminescent text' },
    textSecondary: { $value: '#6ee7b7', $type: 'color', $description: 'Secondary matrix text' },
    textMuted: { $value: '#047857', $type: 'color', $description: 'Muted terminal code' },
    brandPrimary: { $value: '#10b981', $type: 'color', $description: 'Primary emerald accent' },
    brandPrimaryHover: { $value: '#059669', $type: 'color', $description: 'Hover state' },
    brandOnPrimary: { $value: '#05130b', $type: 'color', $description: 'Text on emerald' },
    accentSuccess: { $value: '#34d399', $type: 'color', $description: 'Telemetry normal' },
    accentWarning: { $value: '#fbbf24', $type: 'color', $description: 'Elevated threat' },
    accentDanger: { $value: '#f87171', $type: 'color', $description: 'Perimeter breach' },
    focusRing: { $value: '#10b981', $type: 'color', $description: 'Accessibility ring' },
  },
  geometry: {
    radiusCard: { $value: '8px', $type: 'dimension' },
    radiusControl: { $value: '4px', $type: 'dimension' },
    fontSans: { $value: 'Inter, system-ui, sans-serif', $type: 'fontFamily' },
    fontMono: { $value: '"JetBrains Mono", monospace', $type: 'fontFamily' },
  },
};

export const CYBERPUNK_NEON: ThemeDefinition = {
  themeId: 'cyberpunk-neon',
  displayName: 'Cyberpunk Neon (High Voltage)',
  isDark: true,
  colors: {
    surfaceCanvas: { $value: '#0f051d', $type: 'color', $description: 'Midnight violet canvas' },
    surfacePanel: { $value: '#1a0c30', $type: 'color', $description: 'Neon synth panel' },
    surfacePanelSubtle: { $value: '#261245', $type: 'color', $description: 'Subtle neon container' },
    borderSubtle: { $value: '#3d1b6d', $type: 'color', $description: 'Subtle violet border' },
    borderStrong: { $value: '#f43f5e', $type: 'color', $description: 'Hot rose neon border' },
    textPrimary: { $value: '#ffffff', $type: 'color', $description: 'Pure white headline' },
    textSecondary: { $value: '#f472b6', $type: 'color', $description: 'Neon pink subtitle' },
    textMuted: { $value: '#9d4edd', $type: 'color', $description: 'Muted purple placeholder' },
    brandPrimary: { $value: '#f43f5e', $type: 'color', $description: 'Hot rose neon fill' },
    brandPrimaryHover: { $value: '#e11d48', $type: 'color', $description: 'Hover state' },
    brandOnPrimary: { $value: '#0f051d', $type: 'color', $description: 'High-contrast dark text on rose' },

    accentSuccess: { $value: '#06b6d4', $type: 'color', $description: 'Cyan glow' },
    accentWarning: { $value: '#fbbf24', $type: 'color', $description: 'Amber warning' },
    accentDanger: { $value: '#ff0055', $type: 'color', $description: 'Critical pulse' },
    focusRing: { $value: '#f43f5e', $type: 'color', $description: 'Focus outline' },
  },
  geometry: {
    radiusCard: { $value: '8px', $type: 'dimension' },
    radiusControl: { $value: '4px', $type: 'dimension' },
    fontSans: { $value: 'system-ui, sans-serif', $type: 'fontFamily' },
    fontMono: { $value: 'monospace', $type: 'fontFamily' },
  },
};

export const CANONICAL_PRESETS: readonly ThemeDefinition[] = [
  SOVEREIGN_DARK,
  SOVEREIGN_LIGHT,
  MIDNIGHT_NORDIC,
  INDUSTRIAL_AMBER,
  HIGH_CONTRAST,
  EMERALD_MATRIX,
  CYBERPUNK_NEON,
];

